import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { User, Mail, Calendar, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { SecuritySection } from '../components/SecuritySection';
import { SubscriptionSection } from '../components/SubscriptionSection';
import { ChangePasswordSection } from '../components/ChangePasswordSection';
import { PaymentsSettings } from '../components/PaymentsSettings';

export const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Account Settings</h1>
        <p className="text-zinc-500">Manage your account preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="border-zinc-200">
        <CardHeader>
          <CardTitle className="text-lg">Profile Information</CardTitle>
          <CardDescription>Your account details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="w-8 h-8 text-indigo-600" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900">{user?.name}</p>
              <p className="text-sm text-zinc-500">{user?.email}</p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-500 text-sm">Full Name</Label>
              <div className="flex items-center gap-2 text-zinc-900">
                <User className="w-4 h-4 text-zinc-400" />
                {user?.name}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-500 text-sm">Email Address</Label>
              <div className="flex items-center gap-2 text-zinc-900">
                <Mail className="w-4 h-4 text-zinc-400" />
                {user?.email}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-500 text-sm">Member Since</Label>
              <div className="flex items-center gap-2 text-zinc-900">
                <Calendar className="w-4 h-4 text-zinc-400" />
                {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <SecuritySection />

      {/* Subscription (view plan, cancel / reactivate) */}
      <SubscriptionSection />

      {/* Client Payments (org-level Stripe Connect) */}
      <PaymentsSettings />

      {/* Change password */}
      <ChangePasswordSection />

      {/* Danger Zone */}
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader>
          <CardTitle className="text-lg text-red-900">Sign Out</CardTitle>
          <CardDescription className="text-red-700/70">
            Sign out of your account on this device
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
